import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Zap, 
  CheckCircle2, 
  AlertTriangle, 
  Download, 
  RefreshCw, 
  X, 
  Building2, 
  User, 
  ShieldCheck,
  Check
} from 'lucide-react';
import { WorkOrder } from '../types';
import { FiscalService } from '../../../services/fiscalService';
import { formatCurrencyBRL } from '../../../utils/formatUtils';

interface NfseEmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrder: WorkOrder | null;
  onSuccess?: (result: any) => void;
}

const CODIGOS_TRIBUTACAO_COMUNS = [
  { codigo: '14.01', label: '14.01 — Manutenção, Reparo, Revisão, Mecânica e Equipamentos' },
  { codigo: '04.01', label: '04.01 — Medicina Veterinária, Zootecnia e Atendimento Pet' },
  { codigo: '01.01', label: '01.01 — Informática, TI, Suporte Técnico e Instalação' },
  { codigo: '06.01', label: '06.01 — Barbearia, Cabeleireiros, Estética e Beleza' },
  { codigo: '07.02', label: '07.02 — Execução de Obras, Instalação Elétrica e Hidráulica' },
  { codigo: '17.01', label: '17.01 — Assessoria, Consultoria e Serviços Administrativos' },
  { codigo: 'custom', label: 'Outro código da Lei Complementar 116/03...' }
];

export const NfseEmissionModal: React.FC<NfseEmissionModalProps> = ({
  isOpen,
  onClose,
  workOrder,
  onSuccess,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [emissionSuccess, setEmissionSuccess] = useState<any | null>(null);

  // Campos do Tomador (Cliente)
  const [clientDoc, setClientDoc] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');

  // Campos do Serviço
  const [discriminacao, setDiscriminacao] = useState('');
  const [valorServico, setValorServico] = useState<number>(0);
  const [codigoTributacao, setCodigoTributacao] = useState('14.01');
  const [codigoPersonalizado, setCodigoPersonalizado] = useState('');
  const [issRetido, setIssRetido] = useState<boolean>(false);

  useEffect(() => {
    if (workOrder && isOpen) {
      setErrorMsg(null);
      setEmissionSuccess(null);

      // Preenchimento com dados da OS
      setClientDoc(workOrder.clientDocument || '');
      setClientName(workOrder.clientName || '');
      setClientPhone(workOrder.clientPhone || '');
      setValorServico(workOrder.finalPrice || 0);

      // Constrói a discriminação do serviço a partir dos itens da OS
      let desc = `Prestação de serviços - OS ${workOrder.orderNumber}: ${workOrder.title}`;
      if (workOrder.itemsBreakdown && workOrder.itemsBreakdown.length > 0) {
        const itensServico = workOrder.itemsBreakdown.filter(i => !i.isMaterial);
        const itensParaExibir = itensServico.length > 0 ? itensServico : workOrder.itemsBreakdown;
        const listaDesc = itensParaExibir.map(i => `${i.name} (Qtd: ${i.quantity})`).join('; ');
        desc += `\nServiços executados: ${listaDesc}`;
      }
      if (workOrder.technicalReport) {
        desc += `\nLaudo/Observações: ${workOrder.technicalReport}`;
      }
      setDiscriminacao(desc);
    }
  }, [workOrder, isOpen]);

  if (!isOpen || !workOrder) return null;

  const maskDoc = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 11) {
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
      if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
    }
    if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  };

  const handleEmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const cleanDoc = clientDoc.replace(/\D/g, '');
    if (cleanDoc.length !== 11 && cleanDoc.length !== 14) {
      setErrorMsg('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido para o Tomador do serviço.');
      return;
    }

    if (!clientName.trim()) {
      setErrorMsg('Informe o nome completo ou Razão Social do Tomador.');
      return;
    }

    if (!discriminacao.trim()) {
      setErrorMsg('Informe a discriminação dos serviços prestados.');
      return;
    }

    if (valorServico <= 0) {
      setErrorMsg('O valor dos serviços deve ser maior que zero.');
      return;
    }

    const cTribFinal = codigoTributacao === 'custom' 
      ? codigoPersonalizado.trim() 
      : codigoTributacao;

    if (!cTribFinal) {
      setErrorMsg('Informe o Código de Tributação Nacional da atividade.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        referenceId: workOrder.id,
        tomador: {
          documento: cleanDoc,
          tipoDocumento: (cleanDoc.length === 11 ? 'cpf' : 'cnpj') as 'cpf' | 'cnpj',
          nome: clientName.trim(),
          telefone: clientPhone ? clientPhone.replace(/\D/g, '') : undefined,
          endereco: workOrder.clientAddress ? {
            logradouro: workOrder.clientAddress
          } : undefined,
        },
        servico: {
          codigoTributacaoNacional: cTribFinal,
          discriminacao: discriminacao.trim(),
          valorServico,
          issRetido,
        },
        informacoesComplementares: `Ordem de Serviço Nº ${workOrder.orderNumber}. Sistema R-MAX Precificação Inteligente.`,
      };

      const result = await FiscalService.emitNFSe(payload);
      setEmissionSuccess(result);

      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Falha na emissão da NFS-e Nacional. Verifique a configuração do certificado.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!emissionSuccess?.chaveAcesso) return;
    try {
      await FiscalService.downloadDocumentPdf(
        emissionSuccess.chaveAcesso, 
        `DANFSe_${emissionSuccess.numeroNfse || workOrder.orderNumber}.pdf`
      );
    } catch (err: any) {
      setErrorMsg(err.message || 'Falha ao baixar o PDF da NFS-e.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white border border-zinc-200 w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden my-auto flex flex-col max-h-[92vh]">
        
        {/* Cabeçalho */}
        <div className="p-4 sm:p-5 bg-zinc-900 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-black tracking-tight">Emissão de NFS-e Nacional</h2>
              <p className="text-xs text-zinc-400">Padrão ADN Receita Federal • OS {workOrder.orderNumber}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
          {emissionSuccess ? (
            /* Tela de Sucesso */
            <div className="space-y-4 py-2">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm text-emerald-950">
                    NFS-e Nacional Autorizada com Sucesso!
                  </div>
                  <p className="text-xs text-emerald-800 mt-0.5">
                    O documento fiscal foi transmitido, protocolado e autenticado digitalmente no ambiente nacional da Receita Federal.
                  </p>
                </div>
              </div>

              {/* Detalhes do Documento */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 space-y-2 text-xs">
                <div className="flex justify-between py-1 border-b border-zinc-200/60">
                  <span className="text-zinc-500 font-bold">Número DPS / NFS-e:</span>
                  <span className="font-mono font-black text-zinc-900">
                    {emissionSuccess.numeroNfse || emissionSuccess.numeroDps || '000001'}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-b border-zinc-200/60">
                  <span className="text-zinc-500 font-bold">Protocolo de Autorização:</span>
                  <span className="font-mono text-zinc-900">
                    {emissionSuccess.protocolo || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-b border-zinc-200/60">
                  <span className="text-zinc-500 font-bold">Valor Total dos Serviços:</span>
                  <span className="font-black text-emerald-700">
                    {formatCurrencyBRL(emissionSuccess.valorServico || valorServico)}
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500 font-bold block mb-1">Chave de Acesso Nacional:</span>
                  <span className="font-mono text-[11px] text-zinc-800 break-all bg-white p-2 rounded-lg border border-zinc-200 block">
                    {emissionSuccess.chaveAcesso}
                  </span>
                </div>
              </div>

              {/* Botões pós-emissão */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-700 text-xs font-bold hover:bg-zinc-100 transition-colors cursor-pointer"
                >
                  Fechar
                </button>
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95"
                >
                  <Download className="w-4 h-4" />
                  <span>Baixar DANFSe Oficial (PDF)</span>
                </button>
              </div>
            </div>
          ) : (
            /* Formulário de Emissão */
            <form onSubmit={handleEmit} className="space-y-4">
              {errorMsg && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2.5 text-red-800 text-xs">
                  <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}

              {/* Dados do Tomador */}
              <div className="space-y-2.5">
                <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-zinc-700 border-b pb-1">
                  <User className="w-3.5 h-3.5 text-zinc-500" /> Tomador do Serviço (Cliente)
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs">
                  <div>
                    <label className="block font-bold text-zinc-700 mb-1">CPF ou CNPJ:</label>
                    <input
                      type="text"
                      placeholder="000.000.000-00"
                      value={clientDoc}
                      onChange={e => setClientDoc(maskDoc(e.target.value))}
                      required
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 font-mono text-xs text-zinc-900 focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-zinc-700 mb-1">Telefone (Opcional):</label>
                    <input
                      type="text"
                      placeholder="(00) 00000-0000"
                      value={clientPhone}
                      onChange={e => setClientPhone(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 text-xs text-zinc-900 focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block font-bold text-zinc-700 mb-1">Nome / Razão Social:</label>
                    <input
                      type="text"
                      placeholder="Nome do cliente tomador"
                      value={clientName}
                      onChange={e => setClientName(e.target.value)}
                      required
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 text-xs text-zinc-900 focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>
              </div>

              {/* Dados do Serviço */}
              <div className="space-y-2.5 pt-1">
                <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-zinc-700 border-b pb-1">
                  <FileText className="w-3.5 h-3.5 text-zinc-500" /> Dados do Serviço & Tributação
                </div>

                <div className="space-y-2.5 text-xs">
                  <div>
                    <label className="block font-bold text-zinc-700 mb-1">
                      Código de Tributação Nacional (LC 116/03):
                    </label>
                    <select
                      value={codigoTributacao}
                      onChange={e => setCodigoTributacao(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 text-xs text-zinc-900 focus:bg-white focus:outline-none focus:border-amber-500"
                    >
                      {CODIGOS_TRIBUTACAO_COMUNS.map(c => (
                        <option key={c.codigo} value={c.codigo}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {codigoTributacao === 'custom' && (
                    <div>
                      <label className="block font-bold text-zinc-700 mb-1">
                        Digite o Código de Subitem (ex: 14.02):
                      </label>
                      <input
                        type="text"
                        placeholder="14.02"
                        value={codigoPersonalizado}
                        onChange={e => setCodigoPersonalizado(e.target.value)}
                        required
                        className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 font-mono text-xs text-zinc-900 focus:bg-white focus:outline-none focus:border-amber-500"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block font-bold text-zinc-700 mb-1">
                      Discriminação dos Serviços Prestados:
                    </label>
                    <textarea
                      rows={3}
                      value={discriminacao}
                      onChange={e => setDiscriminacao(e.target.value)}
                      required
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 text-xs text-zinc-900 focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 items-center">
                    <div>
                      <label className="block font-bold text-zinc-700 mb-1">Valor dos Serviços (R$):</label>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 font-bold text-xs">R$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={valorServico}
                          onChange={e => setValorServico(parseFloat(e.target.value) || 0)}
                          required
                          className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 pl-8 font-mono font-bold text-xs text-zinc-900 focus:bg-white focus:outline-none focus:border-amber-500"
                        />
                      </div>
                    </div>

                    <div className="pt-4">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={issRetido}
                          onChange={e => setIssRetido(e.target.checked)}
                          className="w-4 h-4 text-amber-600 rounded border-zinc-300 focus:ring-amber-500 cursor-pointer"
                        />
                        <span className="font-bold text-zinc-700">ISS Retido pelo Tomador</span>
                      </label>
                      <span className="text-[10px] text-zinc-400 block ml-6">
                        Marque se o tomador for responsável tributário.
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rodapé de Ações */}
              <div className="pt-3 border-t border-zinc-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-700 text-xs font-bold hover:bg-zinc-100 transition-colors cursor-pointer"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-zinc-950 text-xs font-black tracking-wide shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Transmitindo para Receita Federal (ADN)...</span>
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      <span>Emitir NFS-e Nacional</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default NfseEmissionModal;
